import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

import useConfigVersionQuery from '@/features/settings/hooks/useConfigVersionQuery';
import { ConfigVersionSummary } from '@/features/settings/hooks/useConfigsQuery';
import { cx } from '@/styles/muiTheme';

interface ConfigHistoryPanelProps {
  configKey: string;
  versions: ConfigVersionSummary[];
  isLoading: boolean;
  expandedVersion: number | null;
  onToggle: (version: number | null) => void;
  onRestore: (version: number) => void;
  /** Protected keys are readable and diffable but never writable. */
  isProtected: boolean;
}

const authorLabel = (entry: ConfigVersionSummary): string => {
  if (!entry.createdBy) {
    // Null either because the row predates the registry (the rollout migration
    // backfilled a baseline with no author) or because the account was deleted.
    return 'unknown';
  }

  const { firstName, lastName, email } = entry.createdBy;
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || email;
};

/** The expanded body of one entry — fetched lazily, cached forever. */
const VersionSnapshot = ({ configKey, version }: { configKey: string; version: number }) => {
  const query = useConfigVersionQuery(configKey, version);

  if (query.isLoading) {
    return (
      <Box sx={{ padding: 2 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Typography variant="caption" sx={{ color: cx.red.main, padding: 2, display: 'block' }}>
        Could not load this version.
      </Typography>
    );
  }

  return (
    <Box
      component="pre"
      sx={{
        margin: 0,
        padding: 1.5,
        fontFamily: cx.fontMono,
        fontSize: 12,
        lineHeight: '18px',
        color: cx.text.tertiary,
        whiteSpace: 'pre-wrap',
        maxHeight: 320,
        overflow: 'auto',
      }}
    >
      {query.data?.rawValue}
    </Box>
  );
};

/**
 * The version list for one config, newest first.
 *
 * Every entry is restorable except the current one. Restoring does not rewind —
 * it copies the chosen version forward as a new version, so the history stays
 * append-only and the restore is itself undoable.
 */
const ConfigHistoryPanel = ({
  configKey,
  versions,
  isLoading,
  expandedVersion,
  onToggle,
  onRestore,
  isProtected,
}: ConfigHistoryPanelProps) => {
  if (isLoading) return <CircularProgress size={20} />;

  if (!versions.length) {
    return (
      <Typography variant="body2" sx={{ color: cx.text.muted }}>
        No history recorded for this key.
      </Typography>
    );
  }

  return (
    <Stack sx={{ gap: 1 }}>
      {versions.map((entry) => {
        const isOpen = expandedVersion === entry.version;

        return (
          <Box
            key={entry.version}
            sx={{
              border: `1px solid ${isOpen ? cx.border.default : cx.border.subtle}`,
              borderRadius: 1,
              backgroundColor: cx.bg.surface2,
            }}
          >
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1, padding: 1, cursor: 'pointer' }}
              onClick={() => onToggle(isOpen ? null : entry.version)}
            >
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}

              <Typography variant="body2" sx={{ fontFamily: cx.fontMono, color: cx.text.primary }}>
                v{entry.version}
              </Typography>

              {entry.isCurrent && (
                <Chip
                  size="small"
                  label="Current"
                  sx={{ backgroundColor: cx.blue.main, color: cx.text.primary, height: 20 }}
                />
              )}

              {entry.restoredFromVersion != null && (
                <Chip
                  size="small"
                  label={`Restored from v${entry.restoredFromVersion}`}
                  sx={{ backgroundColor: cx.bg.elevated, color: cx.text.tertiary, height: 20 }}
                />
              )}

              <Typography variant="caption" sx={{ color: cx.text.tertiary, flexGrow: 1 }}>
                {entry.note || (
                  <Box component="em" sx={{ color: cx.text.muted }}>
                    No reason given
                  </Box>
                )}
              </Typography>

              <Typography variant="caption" sx={{ color: cx.text.muted }}>
                {authorLabel(entry)} · {format(new Date(entry.createdAt), 'M/d/yy, h:mma')}
              </Typography>

              {!entry.isCurrent && !isProtected && (
                <Button
                  size="small"
                  startIcon={<RotateCcw size={13} />}
                  onClick={(event) => {
                    // Without this the row's own onClick also fires and the
                    // entry collapses behind the dialog that just opened.
                    event.stopPropagation();
                    onRestore(entry.version);
                  }}
                  sx={{ color: cx.text.tertiary }}
                >
                  Restore
                </Button>
              )}
            </Stack>

            {isOpen && (
              <Box sx={{ borderTop: `1px solid ${cx.border.subtle}` }}>
                <VersionSnapshot configKey={configKey} version={entry.version} />
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default ConfigHistoryPanel;
